import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState, StatusBadge, fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Plus, ArrowRight, CalendarClock } from "lucide-react";
import { toast } from "sonner";

export default function Assessments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [win, setWin] = useState(null);

  const load = () => api.get("/assessments").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/config/window").then((r) => setWin(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/assessments", { title });
      toast.success("Assessment created");
      setOpen(false);
      setTitle("");
      navigate(`/assessments/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  if (!items) return <Loader label="Loading assessments" />;
  const isTac = user.role === "tac";

  return (
    <div>
      <PageHeader
        overline="Assessment management"
        title={isTac ? "My Assessments" : "All Assessments"}
        description={isTac ? "Start a new attempt, continue drafts, or review submitted versions." : "Oversight of every partner assessment attempt across the platform."}
        actions={isTac && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-assessment-button" className="rounded-md">
                <Plus className="h-4 w-4 mr-1.5" aria-hidden="true" /> New assessment
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-md">
              <DialogHeader><DialogTitle className="font-heading">Start a new assessment</DialogTitle></DialogHeader>
              <div className="py-2">
                <Label htmlFor="title">Assessment title</Label>
                <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="FY2026 Partner Assessment" className="mt-1.5 rounded-md" data-testid="assessment-title-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
                <Button onClick={create} disabled={!title || creating} data-testid="create-assessment-confirm" className="rounded-md">Create &amp; open</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {win?.active && win?.close_date && (() => {
        const daysLeft = Math.ceil((new Date(win.close_date) - new Date()) / 86400000);
        const closed = daysLeft < 0;
        return (
          <div data-testid="deadline-banner" className={`mb-5 flex items-start gap-3 rounded-md border px-4 py-3 text-sm ${closed ? "border-slate-300 bg-slate-50 text-slate-700" : daysLeft <= 3 ? "border-rose-300 bg-rose-50 text-rose-800" : "border-blue-300 bg-blue-50 text-blue-800"}`}>
            <CalendarClock className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
            <div>
              <span className="font-semibold">
                {closed ? "Assessment window closed" : `Assessment window closes ${win.close_date}`}
                {!closed && <span className="font-mono"> · {daysLeft} day{daysLeft === 1 ? "" : "s"} left</span>}
              </span>
              {win.message && <div className="mt-0.5 opacity-90">{win.message}</div>}
            </div>
          </div>
        );
      })()}

      {items.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No assessments yet"
          description={isTac ? "Start your first assessment to begin the workflow." : "No partner assessments have been created yet."}
          action={isTac && <Button onClick={() => setOpen(true)} className="rounded-md">Start assessment</Button>}
        />
      ) : (
        <div className="bg-card border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-xs uppercase tracking-wider">Title</TableHead>
                {!isTac && <TableHead className="text-xs uppercase tracking-wider">Partner</TableHead>}
                <TableHead className="text-xs uppercase tracking-wider">Version</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Updated</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((a, i) => (
                <motion.tr
                  key={a.id}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  className="border-b border-border/60 hover:bg-muted/40 transition-colors cursor-pointer"
                  onClick={() => navigate(`/assessments/${a.id}`)}
                  data-testid={`assessment-row-${a.id}`}
                >
                  <TableCell className="font-medium py-3">{a.title}</TableCell>
                  {!isTac && <TableCell className="py-3 text-sm">{a.tac_org || a.tac_name}</TableCell>}
                  <TableCell className="py-3 font-mono text-sm">{a.current_version > 0 ? `v${a.current_version}` : "—"}</TableCell>
                  <TableCell className="py-3"><StatusBadge status={a.status} /></TableCell>
                  <TableCell className="py-3 text-sm text-muted-foreground font-mono">{fmtDate(a.updated_at)}</TableCell>
                  <TableCell className="py-3 text-right pr-4"><ArrowRight className="h-4 w-4 text-muted-foreground inline" aria-hidden="true" /></TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
